import { FormItem, Input, Label, Text, TextArea } from '@ui5/webcomponents-react';
import { SpecificQueryType } from 'components/shared/interfaces/SpecificQueryTypes';
import {
  IssueType,
  NonAbapQueryDetailsFragment,
  useUpdateNonAbapQueryMutation
} from 'generated/graphql';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateBaseQueryStore } from 'stores/UpdateBaseQueryStore';
import { useUpdateNonAbapQueryStore } from 'stores/UpdateNonAbapQueryStore';
import { useEditQueryStore } from 'stores/EditQueryStore';
import { validateString } from 'utils/validator';
import { FormDisplayItem } from '..';
import { useStyles } from '../DisplayQuery.jss';
import dayjs from 'dayjs';

import WrappingType from '@ui5/webcomponents/dist/types/WrappingType.js';

export interface DisplayQueryDetailsNonAbapProps {
  queryId: string;
  queryDetails: NonAbapQueryDetailsFragment;
}

export const DisplayQueryDetailsNonAbap = forwardRef<
  SpecificQueryType,
  DisplayQueryDetailsNonAbapProps
>((props: DisplayQueryDetailsNonAbapProps, ref) => {
  const { queryDetails, queryId } = props;
  const { t } = useTranslation();
  const nonAbapStore = useUpdateNonAbapQueryStore();
  const baseQueryStore = useUpdateBaseQueryStore();
  const { editMode, setCancelEditing } = useEditQueryStore();
  const [, callUpdateQuery] = useUpdateNonAbapQueryMutation();
  const classes = useStyles();

  useImperativeHandle(ref, () => ({
    cancel: () => {
      nonAbapStore.setInitialStateWithProps(queryDetails);
    },
    save: () => {
      return saveQuery();
    },
    isValid: () => {
      return validateAllFields();
    }
  }));

  useEffect(() => {
    nonAbapStore.setInitialStateWithProps(queryDetails);
  }, []);

  const validateAllFields = () => {
    const isXtmProjectNameValid = validateXtmProjectName();
    const isRepositoryValid = validateRepository();
    const isCollectionValid = validateCollection();
    const isFileNameValid = validateFileName();
    const isStringIdValid = validateStringId();
    const isSourceContactValid = validateSourceContact();

    return (
      isXtmProjectNameValid &&
      isRepositoryValid &&
      isCollectionValid &&
      isFileNameValid &&
      isStringIdValid &&
      isSourceContactValid
    );
  };

  const validateXtmProjectName = () => {
    return validateString(nonAbapStore.xtmProjectName, nonAbapStore.setXtmProjectNameValueState);
  };

  const validateRepository = () => {
    return validateString(nonAbapStore.repository, nonAbapStore.setRepositoryValueState);
  };

  const validateCollection = () => {
    return validateString(nonAbapStore.collection, nonAbapStore.setCollectionValueState);
  };

  const validateFileName = () => {
    return validateString(nonAbapStore.fileName, nonAbapStore.setFileNameValueState);
  };

  const validateStringId = () => {
    return validateString(nonAbapStore.stringId, nonAbapStore.setStringIdValueState);
  };

  const validateSourceContact = () => {
    return validateString(nonAbapStore.sourceContact, nonAbapStore.setSourceContactValueState);
  };

  const onChangeXtmProjectName = (event: CustomEvent) => {
    setCancelEditing(false);
    const { value } = event.target as HTMLInputElement;
    nonAbapStore.setXtmProjectName(value);
    validateString(value, nonAbapStore.setXtmProjectNameValueState);
  };

  const onChangeRepository = (event: CustomEvent) => {
    setCancelEditing(false);
    const { value } = event.target as HTMLInputElement;
    nonAbapStore.setRepository(value);
    validateString(value, nonAbapStore.setRepositoryValueState);
  };

  const onChangeCollection = (event: CustomEvent) => {
    setCancelEditing(false);
    const { value } = event.target as HTMLInputElement;
    nonAbapStore.setCollection(value);
    validateString(value, nonAbapStore.setCollectionValueState);
  };

  const onChangeFileName = (event: CustomEvent) => {
    setCancelEditing(false);
    const { value } = event.target as HTMLInputElement;
    nonAbapStore.setFileName(value);
    validateString(value, nonAbapStore.setFileNameValueState);
  };

  const onChangeStringId = (event: CustomEvent) => {
    setCancelEditing(false);
    const { value } = event.target as HTMLTextAreaElement;
    nonAbapStore.setStringId(value);
    validateString(value, nonAbapStore.setStringIdValueState);
  };

  const onChangeSourceContact = (event: CustomEvent) => {
    setCancelEditing(false);
    const { value } = event.target as HTMLInputElement;
    nonAbapStore.setSourceContact(value);
    validateString(value, nonAbapStore.setSourceContactValueState);
  };

  const saveQuery = async () => {
    const result = await callUpdateQuery({
      input: {
        id: Number(queryId),
        sourceText: baseQueryStore.sourceText,
        targetText: baseQueryStore.targetText,
        deadline: dayjs(baseQueryStore.deadline).format('YYYY-MM-DD'),
        targetLanguage: baseQueryStore.targetLanguage,
        issueType: baseQueryStore.selectedIssueType as unknown as IssueType,
        details: baseQueryStore.details,
        ...(baseQueryStore.assignee && { assignee: baseQueryStore.assignee }),
        distributions: baseQueryStore.distributions,
        resetedDistributions: baseQueryStore.resetedDistributions,
        collection: nonAbapStore.collection,
        fileName: nonAbapStore.fileName,
        repository: nonAbapStore.repository,
        stringId: nonAbapStore.stringId,
        xtmProjectName: nonAbapStore.xtmProjectName,
        sourceContact: nonAbapStore.sourceContact,
        qeReview: baseQueryStore.qeReview
      }
    });
    return { error: result.error };
  };

  const getDetailsData = (): FormDisplayItem[] => {
    return [
      {
        renderComponent: () => (
          <Input
            value={nonAbapStore.xtmProjectName}
            data-testid="edit-xtm-project-name"
            data-help-id="edit-xtm-project-name"
            className={classes.formLineInput}
            valueState={nonAbapStore.xtmProjectNameValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-xtm-project-name')}
              </Label>
            }
            onChange={onChangeXtmProjectName}
          />
        ),
        dataTestId: 'xtm-project-name',
        data: nonAbapStore.xtmProjectName,
        i18nTag: t('display-query-details-non-abap-xtm-project-name'),
        required: true
      },
      {
        renderComponent: () => (
          <Input
            value={nonAbapStore.repository}
            data-testid="edit-repository"
            data-help-id="edit-repository"
            className={classes.formLineInput}
            valueState={nonAbapStore.repositoryValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-repository')}
              </Label>
            }
            onChange={onChangeRepository}
          />
        ),
        dataTestId: 'repository',
        data: nonAbapStore.repository,
        i18nTag: t('display-query-details-non-abap-repository'),
        required: true
      },
      {
        renderComponent: () => (
          <Input
            value={nonAbapStore.collection}
            data-testid="edit-collection"
            data-help-id="edit-collection"
            className={classes.formLineInput}
            valueState={nonAbapStore.collectionValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-collection')}
              </Label>
            }
            onChange={onChangeCollection}
          />
        ),
        dataTestId: 'collection',
        data: nonAbapStore.collection,
        i18nTag: t('display-query-details-non-abap-collection'),
        required: true
      },
      {
        renderComponent: () => (
          <Input
            value={nonAbapStore.fileName}
            data-testid="edit-file-name"
            data-help-id="edit-file-name"
            className={classes.formLineInput}
            valueState={nonAbapStore.fileNameValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-file-name')}
              </Label>
            }
            onChange={onChangeFileName}
          />
        ),
        dataTestId: 'file-name',
        data: nonAbapStore.fileName,
        i18nTag: t('display-query-details-non-abap-file-name'),
        required: true
      },
      {
        renderComponent: () => (
          <TextArea
            value={nonAbapStore.stringId}
            data-testid="edit-string-id"
            data-help-id="edit-string-id"
            valueState={nonAbapStore.stringIdValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-string-id')}
              </Label>
            }
            onChange={onChangeStringId}
          />
        ),
        dataTestId: 'string-id',
        data: nonAbapStore.stringId,
        i18nTag: t('display-query-details-non-abap-string-id'),
        required: true
      },
      {
        renderComponent: () => (
          <Input
            value={nonAbapStore.sourceContact}
            data-testid="edit-source-contact"
            data-help-id="edit-source-contact"
            className={classes.formLineInput}
            valueState={nonAbapStore.sourceContactValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-source-contact')}
              </Label>
            }
            onChange={onChangeSourceContact}
          />
        ),
        dataTestId: 'source-contact',
        data: queryDetails.sourceContactDisplayName,
        i18nTag: t('display-query-details-non-abap-source-contact'),
        required: true
      }
    ];
  };

  let queryDetailsData: FormDisplayItem[] = getDetailsData();

  useEffect(() => {
    queryDetailsData = getDetailsData();
  }, [queryDetails]);

  return (
    <>
      {queryDetailsData.map((entry) => (
        <FormItem
          key={entry.dataTestId}
          labelContent={
            <Label
              id={entry.i18nTag}
              required={editMode && entry.required}
              data-help-id={entry.dataTestId}
              className={classes.formItemLabel}
              wrappingType="None"
            >
              {entry.i18nTag}
            </Label>
          }
        >
          {editMode && entry.editable !== false ? (
            entry.renderComponent()
          ) : (
            <Text
              data-testid={entry.dataTestId}
              aria-labelledby={entry.i18nTag}
              className={classes.formItemText}
            >
              {entry.data}
            </Text>
          )}
        </FormItem>
      ))}
    </>
  );
});
